import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import DashSummaryHistory from "./DashSummaryHistory";
import DashWorkingHistory from "./DashWorkingHistory";
import AdminRequestNotifications from "./AdminRequestNotifications";
import { requestTypes, status, rembursment } from "../../utils/Helpers";
import _ from "lodash";
import { getPayrollRequests, getReimbursementRequest } from "../../services";
import { AppContext } from "../../utils/AppContext";
import { useSelector } from "react-redux";

const DashboardList = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const userProfile = useSelector((state) => state.user);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState('');
  const [error, setError] = useState(null);

  const { UpadateStatusData } = useContext(AppContext);

  const isAdmin = userProfile?.userinfo?.MemberRole == 2;

  const handleOpenPopup = (content) => {
    const formattedContent = content
      .split(/\s+/)
      .reduce((lines, word, i) => {
        if (i % 8 === 0) {
          lines.push([word]);
        } else {
          lines[lines.length - 1].push(word);
        }
        return lines;
      }, [])
      .map(line => line.join(' '))
      .join('\n');

    setSelectedContent(formattedContent);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  const formatDate = (date, requestType) => {
    const formattedDate = moment(date);
    if (!formattedDate.isValid()) {
      return "";
    }

    switch (requestType) {
      case "Half Day":
        return formattedDate.isSame(moment(), "day")
          ? `Today ${formattedDate.format("h:mm A")}`
          : formattedDate.format("MMM D, h:mm A");
      case "Leave":
        return formattedDate.format("DD/MM/YY");
      case "CheckIn Adjustment":
      case "CheckOut Adjustment":
      case "Long Break":
      case "Overtime":
        return formattedDate.format("DD/MM/YY, HH:mm");
      case "Breaks Adjustment":
        return formattedDate.format("DD/MM/YY");
      case "Reimbursement":
        return moment(date).format("DD/MM/YY");
      default:
        return formattedDate.format("DD/MM/YY");
    }
  };

  const formatDateRange = (startDate, endDate, requestType) => {
    const formattedStartDate = formatDate(startDate, requestType);
    const formattedEndDate = formatDate(endDate, requestType);

    if (!formattedStartDate && !formattedEndDate) {
      return "Invalid Date Range";
    }
    return `${formattedStartDate || ""}  ${formattedEndDate || ""}`;
  };

  const fetchRequest = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const payrollResponse = await getPayrollRequests(userProfile.userinfo.Id, "", "", "", "", "", "2");
      const payrollRequests = payrollResponse?.data?.Result?.RequestList || [];

      const reimbursementResponse = await getReimbursementRequest(
        userProfile.userinfo.Id,
        "",
        "",
        "",
        "",
        0,
        1
      );
      const reimbursementRequests = reimbursementResponse?.data?.Result?.RequestList || [];

      const limitedPayrollRequests = payrollRequests.slice(0, 2);
      const limitedReimbursementRequests = reimbursementRequests.slice(0, 1);

      const allRequests = {
        RequestList: [
          ...limitedPayrollRequests,
          ...limitedReimbursementRequests.map(req => ({
            ...req,
            RequestType: "Reimbursement"
          }))
        ]
      };

      allRequests.RequestList.sort((a, b) =>
        new Date(b.CreatedAt) - new Date(a.CreatedAt)
      );

      setRequests(allRequests);
    } catch (err) {
      setError("Failed to fetch requests. Please try again later.");
      console.error("Error fetching requests:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequest();
  }, [UpadateStatusData]);

  const formatCreatedAt = (date) => moment(date).format("DD/MM/YYYY, h:mm A");

  const getStatusStyles = (requestStatus) => {
    const baseStyles =
      "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border";
    switch (requestStatus) {
      case 1:
        return `${baseStyles} bg-white/10 border-gray-400/20 text-gray-700 dark:bg-white/5 dark:border-gray-300/15 dark:text-gray-200`;
      case 2:
        return `${baseStyles} bg-red-500/15 border-red-400/25 text-red-700 dark:text-red-200 dark:bg-red-500/10`;
      case 3:
        return `${baseStyles} bg-green-500/15 border-green-400/25 text-green-700 dark:text-green-200 dark:bg-green-500/10`;
      default:
        return `${baseStyles} bg-white/10 border-gray-400/20 text-gray-700 dark:bg-white/5 dark:border-gray-300/15 dark:text-gray-200`;
    }
  };

  return (
    <div className="xl:flex xl:space-x-3 mb-6 mt-2">
      <div className="xl:w-[61%]">
        <div className="relative mt-1 group/dashboard-request-history">
          <div
            className="absolute -inset-[1px] bg-gradient-to-r from-blue-600/40 via-indigo-600/40 to-blue-600/40 rounded-2xl blur-sm opacity-50 group-hover/dashboard-request-history:opacity-80 transition duration-500 pointer-events-none"
            aria-hidden="true"
          />
          <div className="relative bg-white/70 dark:bg-black/40 backdrop-blur-3xl rounded-2xl min-h-56 border-2 border-blue-500/20 dark:border-blue-400/20 shadow-[0_15px_35px_-10px_rgba(0,0,0,0.4)] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-blue-500/10">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Request History</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Track your requests</p>
            </div>
            <button
              onClick={fetchRequest}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <div className="text-red-500 dark:text-red-400 text-sm mb-2">{error}</div>
              <button
                onClick={fetchRequest}
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-white/5 dark:bg-white/5 text-left">
                    <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">DATE</th>
                    <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">TYPE</th>
                    <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">DETAILS</th>
                    <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">STATUS</th>
                    <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">CREATED</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {!_.isEmpty(requests?.RequestList) ? (
                    requests?.RequestList.map((request) => (
                      <tr
                        key={request.id}
                        className="hover:bg-white/[0.06] dark:hover:bg-white/[0.08] transition-colors"
                      >
                        <td className="px-4 py-2.5 text-sm text-gray-900 dark:text-gray-200">
                          {request.RequestType === "Reimbursement"
                            ? formatDate(request.Date, request.RequestType)
                            : formatDateRange(request.StartDate, request.EndDate, request.RequestType)}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-gray-900 dark:text-gray-200">
                          {request.RequestType === "Reimbursement"
                            ? rembursment[request.RequestSubType] || "Reimbursement"
                            : requestTypes[request.RequestType]}
                        </td>
                        <td className="px-4 py-2.5 text-sm">
                          {request.RequestDetail.length > 30 ? (
                            <button
                              onClick={() => handleOpenPopup(request.RequestDetail)}
                              className="text-blue-600 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200 font-semibold text-sm"
                            >
                              View Details
                            </button>
                          ) : (
                            <span className="text-gray-900 dark:text-gray-200">{request.RequestDetail}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={getStatusStyles(request.RequestStatus)}>
                            <span
                              className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                request.RequestStatus === 1
                                  ? "bg-gray-400"
                                  : request.RequestStatus === 2
                                  ? "bg-red-400"
                                  : request.RequestStatus === 3
                                  ? "bg-green-400"
                                  : "bg-gray-400"
                              }`}
                            />
                            {status[request.RequestStatus]}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-sm text-gray-900 dark:text-gray-200">
                          {formatCreatedAt(request.CreatedAt)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center">
                        <div className="text-gray-500 dark:text-gray-400">
                          <svg className="w-6 h-6 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-sm font-medium">No requests available</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          </div>
        </div>
        <DashWorkingHistory />
      </div>
      {isAdmin ? <AdminRequestNotifications /> : <DashSummaryHistory />}

      {isPopupOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/70 dark:bg-black/40 backdrop-blur-3xl border-2 border-blue-500/20 dark:border-blue-400/20 rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] max-w-lg w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-blue-500/10">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Request Details
              </h3>
              <button
                onClick={handleClosePopup}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <div className="bg-white/10 dark:bg-white/[0.04] border border-blue-500/10 rounded-xl p-4">
                <p className="text-sm text-gray-900 dark:text-gray-300 whitespace-pre-wrap">
                  {selectedContent}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardList;
